import * as React from 'react';
import { useEffect, useState } from 'react';
import { Breadcrumbs, CircularProgress, Link } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Stripe from "../dashboard-components/Stripe";
import { ConnectComponentsProvider, ConnectPayouts } from "@stripe/react-connect-js";
import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";
import GeneralApiService from "../../axios/Axios";
import { getStripePublishableKey } from "../../stripeConfig";

interface Franchise {
    location: string;
    stripe_account_linked: boolean;
}

export default function Payouts() {
    const [franchise, setFranchise] = useState<Franchise | null>(null);
    const [loadedData, setLoadingData] = useState(true);
    const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);

    useEffect(() => {
        setLoadingData(true);
        const getFranchise = async () => {
            try {
                const ads = await GeneralApiService.getToggledAds();
                setFranchise(ads);
            } catch (error) {
                console.error("Failed to fetch franchise data:", error);
                toast.error("Could not load franchise information.");
            } finally {
                setLoadingData(false);
            }
        };
        getFranchise();
    }, []);

    useEffect(() => {
        if (franchise && franchise.stripe_account_linked && !stripeConnectInstance) {
            const fetchClientSecret = async () => {
                try {
                    const tokenData = await GeneralApiService.getStripePayouts();
                    return tokenData?.token?.client_secret;
                } catch (error) {
                    console.error("Failed to fetch Stripe client secret:", error);
                    toast.error("Could not connect to Stripe payouts.");
                    return undefined;
                }
            };

            const initializeStripe = async () => {
                const connectInstance = await loadConnectAndInitialize({
                    publishableKey: getStripePublishableKey(),
                    fetchClientSecret: fetchClientSecret,
                });
                setStripeConnectInstance(connectInstance);
            };

            initializeStripe();
        }
    }, [franchise, stripeConnectInstance]);

    return (
        <>
            <ToastContainer />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon /></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Payouts</Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ display: 'flex', my: 1, gap: 1, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Typography level="h2" component="h1">Payouts</Typography>
            </Box>

            {loadedData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <>
                    {franchise && !franchise.stripe_account_linked && <Stripe franchise={franchise} />}
                    {franchise && franchise.stripe_account_linked && stripeConnectInstance && (
                        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                            <ConnectPayouts />
                        </ConnectComponentsProvider>
                    )}
                </>
            )}
        </>
    );
}